/**
 * Floorplan list v2: FLYOUT / KEYBOARD / ARIA DISCLOSURE.
 * Does not handle filtering logic (see floorplan-list.js)
 *
 * Keyboard: ArrowDown/Up on .filter-btn opens (single flyout) and focuses first/last tabbable in .filter-dropdown; same when already open.
 * Inside open .filter-dropdown: ArrowUp/Down wrap; Home/End to first/last (only if activeElement is already a tabbable in that panel).
 * Escape closes and returns focus to the filter button (aria-labelledby on panel, else last opened .filter-btn).
 * Enter/Space on .filter-btn: native activation via click only (no duplicate keydown toggle). Tab: no trap.
 * When focus leaves a .floorplan-list-filter column for a known target outside it (focusout relatedTarget), that column’s open flyout closes synchronously.
 * If relatedTarget is null (common for mouse moves), do not close from focusout — outside clicks still run closeAll on document; avoids closing before the click lands on a control.
 * Shift+Tab back to a .filter-btn does not auto-reopen a closed panel.
 */
(function () {
    const filterToolbar = document.getElementById('floorplan-list-filters-container');
    if (!filterToolbar) {
        return;
    }

    function checkboxesInFilterDropdown(filterDropdown) {
        if (!filterDropdown || filterDropdown.hasAttribute('hidden')) {
            return [];
        }
        return Array.prototype.filter.call(
            filterDropdown.querySelectorAll('input:not([disabled])[type="checkbox"]'),
            function (checkbox) {
                if (checkbox.disabled || checkbox.closest('[hidden]')) {
                    return false;
                }
                const computed = window.getComputedStyle(checkbox);
                if (computed.visibility === 'hidden' || computed.display === 'none') {
                    return false;
                }
                return true;
            },
        );
    }

    function closeAllFilterDropdowns() {
        filterToolbar.querySelectorAll('.filter-dropdown').forEach(function (filterDropdown) {
            filterDropdown.setAttribute('hidden', '');
        });
        filterToolbar.querySelectorAll('.filter-btn').forEach(function (filterButton) {
            filterButton.setAttribute('aria-expanded', 'false');
        });
    }

    function closeFilterDropdown(filterColumn) {
        const filterDropdown = filterColumn.querySelector('.filter-dropdown');
        const filterButton = filterColumn.querySelector('.filter-btn');
        if (filterDropdown) {
            filterDropdown.setAttribute('hidden', '');
        }
        if (filterButton) {
            filterButton.setAttribute('aria-expanded', 'false');
        }
    }

    function openFilterDropdown(filterDropdown, filterButton) {
        closeAllFilterDropdowns();
        filterDropdown.removeAttribute('hidden');
        filterButton.setAttribute('aria-expanded', 'true');
    }

    let lastFilterButton = null;

    // Toggle flyout open/closed when a .filter-btn is clicked; stop clicks inside .filter-dropdown from closing via document handler.
    filterToolbar.addEventListener('click', function (clickEvent) {
        if (clickEvent.target.closest('.filter-dropdown')) {
            clickEvent.stopPropagation();
        }
        const filterButton = clickEvent.target.closest('.filter-btn');
        if (!filterButton || !filterToolbar.contains(filterButton)) {
            return;
        }
        clickEvent.preventDefault();
        const filterDropdownId = filterButton.getAttribute('aria-controls');
        const filterDropdown = filterDropdownId ? document.getElementById(filterDropdownId) : null;
        if (!filterDropdown) {
            return;
        }
        const isExpanded = filterButton.getAttribute('aria-expanded') === 'true';
        if (isExpanded) {
            closeFilterDropdown(filterButton.closest('.floorplan-list-filter'));
        } else {
            openFilterDropdown(filterDropdown, filterButton);
            lastFilterButton = filterButton;
        }
    });

    // Close every flyout when the user clicks outside the filter toolbar.
    document.addEventListener('click', function (clickEvent) {
        if (filterToolbar.contains(clickEvent.target)) {
            return;
        }
        closeAllFilterDropdowns();
    });

    // Escape: close the open flyout and return focus to its .filter-btn (aria-labelledby), else last opened .filter-btn.
    document.addEventListener('keydown', function (keydownEvent) {
        if (keydownEvent.key !== 'Escape') {
            return;
        }
        const visibleFilterDropdown = filterToolbar.querySelector('.filter-dropdown:not([hidden])');
        if (!visibleFilterDropdown) {
            return;
        }
        const filterButtonId = visibleFilterDropdown.getAttribute('aria-labelledby');
        const filterButton = filterButtonId && document.getElementById(filterButtonId);
        closeAllFilterDropdowns();
        if (filterButton && filterButton.classList.contains('filter-btn')) {
            filterButton.focus();
        } else if (lastFilterButton) {
            lastFilterButton.focus();
        }
    });

    // When focus moves to another focusable outside this .floorplan-list-filter column, close that column’s flyout (skip null relatedTarget, e.g. mouse).
    filterToolbar.querySelectorAll('.floorplan-list-filter').forEach(function (filterColumn) {
        filterColumn.addEventListener(
            'focusout',
            function (focusOutEvent) {
                const related = focusOutEvent.relatedTarget;
                if (related instanceof Element && filterColumn.contains(related)) {
                    return;
                }
                if (!(related instanceof Element)) {
                    return;
                }
                const filterDropdown = filterColumn.querySelector('.filter-dropdown');
                if (filterDropdown && !filterDropdown.hasAttribute('hidden')) {
                    closeFilterDropdown(filterColumn);
                }
            },
            true,
        );
    });

    // Arrow keys on .filter-btn open/focus panel checkboxes; Arrow/Home/End inside an open .filter-dropdown move focus among checkboxes.
    filterToolbar.addEventListener('keydown', function (keydownEvent) {
        const filterButton = keydownEvent.target.closest('.filter-btn');
        if (
            filterButton &&
            filterToolbar.contains(filterButton) &&
            (keydownEvent.key === 'ArrowDown' || keydownEvent.key === 'ArrowUp')
        ) {
            const filterDropdownId = filterButton.getAttribute('aria-controls');
            const filterDropdown = filterDropdownId ? document.getElementById(filterDropdownId) : null;
            if (!filterDropdown) {
                return;
            }
            keydownEvent.preventDefault();
            const alreadyExpanded = filterButton.getAttribute('aria-expanded') === 'true';
            if (!alreadyExpanded) {
                openFilterDropdown(filterDropdown, filterButton);
            }
            lastFilterButton = filterButton;
            const checkboxesInOpenDropdown = checkboxesInFilterDropdown(filterDropdown);
            if (checkboxesInOpenDropdown.length === 0) {
                return;
            }
            if (keydownEvent.key === 'ArrowDown') {
                checkboxesInOpenDropdown[0].focus();
            } else {
                checkboxesInOpenDropdown[checkboxesInOpenDropdown.length - 1].focus();
            }
            return;
        }

        const filterDropdown = keydownEvent.target.closest('.filter-dropdown');
        if (!filterDropdown || !filterToolbar.contains(filterDropdown) || filterDropdown.hasAttribute('hidden')) {
            return;
        }
        const navigationKeys = ['ArrowDown', 'ArrowUp', 'Home', 'End'];
        if (navigationKeys.indexOf(keydownEvent.key) === -1) {
            return;
        }
        const checkboxesInOpenDropdown = checkboxesInFilterDropdown(filterDropdown);
        if (checkboxesInOpenDropdown.length === 0) {
            return;
        }
        const currentIndex = checkboxesInOpenDropdown.indexOf(document.activeElement);
        if (currentIndex === -1) {
            return;
        }
        keydownEvent.preventDefault();
        if (keydownEvent.key === 'Home') {
            checkboxesInOpenDropdown[0].focus();
            return;
        }
        if (keydownEvent.key === 'End') {
            checkboxesInOpenDropdown[checkboxesInOpenDropdown.length - 1].focus();
            return;
        }
        let nextIndex = keydownEvent.key === 'ArrowDown' ? currentIndex + 1 : currentIndex - 1;
        if (nextIndex < 0) {
            nextIndex = checkboxesInOpenDropdown.length - 1;
        }
        if (nextIndex >= checkboxesInOpenDropdown.length) {
            nextIndex = 0;
        }
        checkboxesInOpenDropdown[nextIndex].focus();
    });
})();
